import * as styles from "./CommandPalette.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  type KeyboardEvent,
  type Ref,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  Dialog,
  DialogContent,
  fadeOnly,
} from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Kbd } from "@frontend/components/atoms/Kbd";
import { useKeyboardShortcut } from "@frontend/hooks/useKeyboardShortcut";
import { useLingui } from "@lingui/react";

import { matchCommands } from "./commands/registry";
import type { Command } from "./commands/types";
import { CommandItem } from "./components/CommandItem";
import { EmptyState } from "./components/EmptyState";
import { FilterChip } from "./components/FilterChip";
import { PaletteFooter } from "./components/PaletteFooter";
import { QueryExplanation } from "./components/QueryExplanation";
import { ResultsList } from "./components/ResultsList";
import { SearchResultItem } from "./components/SearchResultItem";
import { TypeAutocomplete } from "./components/TypeAutocomplete";
import { getTypeAliasSuggestions } from "./dsl/aliases";
import { parseToken } from "./dsl/parseToken";
import { useGlobalSearch } from "./hooks/useGlobalSearch";
import { commandPaletteLabels } from "./i18n/labels";
import { getResultHref, getResultTypeDefinition } from "./results/registry";
import type { SearchHit } from "./results/types";
import {
  addTokenAtom,
  commandQueryAtom,
  filterTokensAtom,
  hideAtom,
  highlightedIndexAtom,
  isVisibleAtom,
  modeAtom,
  moveHighlightAtom,
  queryAtom,
  removeLastTokenAtom,
  removeTokenAtom,
  selectedIdsAtom,
  selectedTypesAtom,
  showAtom,
  toggleAtom,
} from "./stores";

const TYPE_PREFIX = "type:";

export interface CommandPaletteHandle {
  show: () => CommandPaletteHandle;
  hide: () => CommandPaletteHandle;
  toggle: () => CommandPaletteHandle;
}

export type CommandPaletteProps = {
  ref?: Ref<CommandPaletteHandle>;
};

function CommandPaletteContent({ ref }: CommandPaletteProps) {
  const { _ } = useLingui();

  const isVisible = useAtomValue(isVisibleAtom);
  const [query, setQuery] = useAtom(queryAtom);
  const mode = useAtomValue(modeAtom);
  const commandQuery = useAtomValue(commandQueryAtom);
  const filterTokens = useAtomValue(filterTokensAtom);
  const selectedTypes = useAtomValue(selectedTypesAtom);
  const selectedIds = useAtomValue(selectedIdsAtom);
  const highlightedIndex = useAtomValue(highlightedIndexAtom);
  const setHighlightedIndex = useSetAtom(highlightedIndexAtom);
  const moveHighlight = useSetAtom(moveHighlightAtom);
  const addToken = useSetAtom(addTokenAtom);
  const removeToken = useSetAtom(removeTokenAtom);
  const removeLastToken = useSetAtom(removeLastTokenAtom);
  const show = useSetAtom(showAtom);
  const hide = useSetAtom(hideAtom);
  const toggle = useSetAtom(toggleAtom);

  const inputRef = useRef<HTMLInputElement>(null);

  useKeyboardShortcut("mod+k", toggle);

  const { hits, loading, totalHits } = useGlobalSearch(
    query,
    selectedTypes,
    selectedIds,
    isVisible && "search" === mode,
  );

  const commands = "command" === mode ? matchCommands(commandQuery) : [];
  const itemCount = "command" === mode ? commands.length : hits.length;

  const handle: CommandPaletteHandle = {
    show: () => {
      show();
      return handle;
    },
    hide: () => {
      hide();
      return handle;
    },
    toggle: () => {
      toggle();
      return handle;
    },
  };

  useImperativeHandle(ref, () => handle, []);

  const runCommand = (command: Command) => {
    command.run({ close: hide });
  };

  const openHit = (hit: SearchHit) => {
    const href = getResultHref(hit);

    if (!href) {
      console.debug("No navigation target for hit:", hit);
      return;
    }

    hide();
    window.location.href = href;
  };

  const handleSelectIndex = (index: number) => {
    if ("command" === mode) {
      const command = commands[index];

      if (command) {
        runCommand(command);
      }
    } else {
      const hit = hits[index];

      if (hit) {
        openHit(hit);
      }
    }
  };

  const currentWord = query.slice(query.lastIndexOf(" ") + 1);

  const typeAutocompleteOpen =
    "search" === mode && currentWord.toLowerCase().startsWith(TYPE_PREFIX);

  const typeAutocompleteOptions = typeAutocompleteOpen
    ? getTypeAliasSuggestions()
        .filter(({ alias }) =>
          alias.startsWith(currentWord.slice(TYPE_PREFIX.length).toLowerCase()),
        )
        .map(({ alias, value }) => ({
          alias,
          value,
          // getTypeAliasSuggestions() only lists values already present in
          // resultTypeRegistry, so a definition always exists here.
          label: _(getResultTypeDefinition(value)!.label),
        }))
    : [];

  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const [autocompleteWord, setAutocompleteWord] = useState(currentWord);

  if (currentWord !== autocompleteWord) {
    setAutocompleteWord(currentWord);
    setAutocompleteIndex(0);
  }

  const applyTypeToken = (value: string) => {
    const words = query.split(/\s+/);
    setQuery(words.slice(0, -1).join(" "));
    addToken({ key: "type", value });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (typeAutocompleteOptions.length > 0) {
      if ("ArrowDown" === event.key) {
        event.preventDefault();
        setAutocompleteIndex(
          (index) => (index + 1) % typeAutocompleteOptions.length,
        );
        return;
      }

      if ("ArrowUp" === event.key) {
        event.preventDefault();
        setAutocompleteIndex(
          (index) =>
            (index - 1 + typeAutocompleteOptions.length) %
            typeAutocompleteOptions.length,
        );
        return;
      }

      if ("Enter" === event.key || "Tab" === event.key) {
        const option = typeAutocompleteOptions[autocompleteIndex];

        if (option) {
          event.preventDefault();
          applyTypeToken(option.value);
          return;
        }
      }
    }

    if ("search" === mode && " " === event.key) {
      const words = query.split(/\s+/);
      const lastWord = words[words.length - 1] ?? "";
      const token = parseToken(lastWord);

      if (token) {
        event.preventDefault();
        setQuery(words.slice(0, -1).join(" "));
        addToken(token);
        return;
      }
    }

    if ("search" === mode && "Backspace" === event.key && "" === query) {
      if (filterTokens.length > 0) {
        event.preventDefault();
        removeLastToken();
      }
      return;
    }

    if ("ArrowDown" === event.key) {
      event.preventDefault();
      moveHighlight({ delta: 1, count: itemCount });
    } else if ("ArrowUp" === event.key) {
      event.preventDefault();
      moveHighlight({ delta: -1, count: itemCount });
    } else if ("Enter" === event.key) {
      event.preventDefault();

      if (itemCount > 0) {
        handleSelectIndex(highlightedIndex);
      }
    }
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          hide();
        }
      }}
    >
      <DialogContent
        size="lg"
        initialFocus={inputRef}
        transition={fadeOnly}
        style={{ top: "12vh", transform: "translateX(-50%)" }}
      >
        <div className={styles.inputArea}>
          <div className={styles.inputRow}>
            <Icon name="Search" className={styles.searchIcon} />

            {"search" === mode &&
              filterTokens.map((token, index) => (
                <FilterChip
                  key={`${token.key}:${token.value}`}
                  token={token}
                  onRemove={() => removeToken(index)}
                />
              ))}

            <Input
              ref={inputRef}
              size="lg"
              variant="ghost"
              value={query}
              onValueChange={setQuery}
              onKeyDown={handleKeyDown}
              placeholder={_(
                "command" === mode
                  ? commandPaletteLabels.commandPlaceholder
                  : commandPaletteLabels.searchPlaceholder,
              )}
              className={styles.input}
            />
            <Kbd>esc</Kbd>
          </div>

          {"search" === mode && (
            <TypeAutocomplete
              options={typeAutocompleteOptions}
              highlightedIndex={autocompleteIndex}
              onHighlight={setAutocompleteIndex}
              onSelect={applyTypeToken}
            />
          )}
        </div>

        {"search" === mode && (
          <QueryExplanation
            tokens={filterTokens}
            freeText={query}
            resultCount={hits.length}
            totalCount={totalHits}
          />
        )}

        {"command" === mode ? (
          commands.length > 0 ? (
            <ResultsList
              items={commands}
              itemKey={(command) => command.id}
              highlightedIndex={highlightedIndex}
              onHighlight={setHighlightedIndex}
              onSelect={(command) => runCommand(command)}
              estimateSize={() => 44}
            >
              {(command) => <CommandItem command={command} />}
            </ResultsList>
          ) : (
            <EmptyState>{_(commandPaletteLabels.noCommands)}</EmptyState>
          )
        ) : hits.length > 0 ? (
          <ResultsList
            items={hits}
            itemKey={(hit) => `${hit._federation.indexUid}-${hit.id}`}
            highlightedIndex={highlightedIndex}
            onHighlight={setHighlightedIndex}
            onSelect={(hit) => openHit(hit)}
            estimateSize={() => 56}
          >
            {(hit, _index, highlighted) => (
              <SearchResultItem hit={hit} highlighted={highlighted} />
            )}
          </ResultsList>
        ) : (query.trim() || filterTokens.length > 0) && !loading ? (
          <EmptyState>{_(commandPaletteLabels.noResults)}</EmptyState>
        ) : null}

        {"search" === mode && <PaletteFooter />}
      </DialogContent>
    </Dialog>
  );
}

export function CommandPalette({ ref }: CommandPaletteProps) {
  return <CommandPaletteContent ref={ref} />;
}
